
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PlusCircle, TrendingUp, Database, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import StatCard from '../StatCard';
import { useRoleDashboardStats } from '@/hooks/use-role-dashboard-stats';

const BenchmarkDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { totalEntries, todayEntries, marketsTracked, recentEntries, isLoading } = useRoleDashboardStats('benchmark');

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    if (diffInDays === 1) return '1 day ago';
    return `${diffInDays} days ago`;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Price Data Dashboard</h1>
          <p className="text-muted-foreground">
            Manage and track price data entries across different markets
          </p>
        </div>
        <Button onClick={() => navigate('/price-benchmarking/data-entry')} className="gap-2">
          <PlusCircle className="h-4 w-4" />
          Add Price Data
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Entries"
          value={isLoading ? '--' : totalEntries?.toString() || '0'}
          icon={<Database className="h-4 w-4" />}
        />
        <StatCard
          title="Today's Entries"
          value={isLoading ? '--' : todayEntries?.toString() || '0'}
          icon={<Calendar className="h-4 w-4" />}
        />
        <StatCard
          title="Markets Tracked"
          value={isLoading ? '--' : marketsTracked?.toString() || '0'}
          icon={<TrendingUp className="h-4 w-4" />}
        />
        <StatCard
          title="Data Sources"
          value="5"
          icon={<Database className="h-4 w-4" />}
        />
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
          <CardDescription>Your latest price data entries across all markets</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="flex items-center justify-between py-2 border-b">
                    <div className="space-y-1">
                      <div className="h-4 bg-muted rounded w-48"></div>
                      <div className="h-3 bg-muted rounded w-32"></div>
                    </div>
                    <div className="h-3 bg-muted rounded w-16"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : recentEntries && recentEntries.length > 0 ? (
            <div className="space-y-3">
              {recentEntries.map((entry: any, index: number) => (
                <div key={index} className="flex items-center justify-between py-2 border-b last:border-b-0">
                  <div>
                    <p className="font-medium">{entry.product_name} - {entry.source_type}</p>
                    <p className="text-sm text-muted-foreground">
                      {entry.price} ETB/kg - {entry.location}
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {formatTimeAgo(entry.created_at)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No price data entries yet</p>
              <p className="text-sm">Start by adding your first price entry</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BenchmarkDashboard;
